import { AccordionDetails, AccordionSummary, Box, Typography, useTheme } from '@mui/material';
import Divider from 'components/divider';
import { ChevronDown } from 'lucide-react';

import { useMemo } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import LineDescription from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/components/input-settings/feed-reserve/line-description';
import { useInputSettingsState } from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/context/input-settings-context';
import {
  bufferKgValue,
  calculateMaxDays,
  calculateZIndexValues,
  increasedDays,
} from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/utils/feed-reserve-calculations';
import {
  Accordion,
  AccordionGroup,
  BufferFill,
  BufferText,
  OutlineText,
} from 'views/order-feed/features/my-supply-plan/features/ofd-2.0/utils/styled-components';

type SliderProps = {
  bufferValue: number;
  newValue: any;
};

export type CreateFeedReserveForm = {
  dailyConsumtionRate: number;
  maxSiloCapacity: number | undefined;
};

const PAGE_NAME_ID = 'orderFeed.ofdMySupplyPlan.inputSettings.feedReserveTab';

const FeedReserve: React.FC<SliderProps> = () => {
  const theme = useTheme();
  const {
    getOfdSettingsQuery: { data: ofdSettings },
  } = useInputSettingsState();

  const maxSiloCapacity = ofdSettings?.siloCapacity ?? 0;
  const dailyConsumptionRate = ofdSettings?.currentConsumptionRate ?? 0;
  const days = ofdSettings?.consumptionBufferDays ?? 0;
  const bufferValue = ((ofdSettings?.capacityBuffer ?? 0) / 100) * 100;

  const staticBufferKg = bufferKgValue(maxSiloCapacity, bufferValue);
  const maxDays = calculateMaxDays(maxSiloCapacity, dailyConsumptionRate);
  const zIndexValues = useMemo(() => calculateZIndexValues(bufferValue, maxDays, days), [bufferValue, maxDays, days]);
  const daysInKg = increasedDays(days, dailyConsumptionRate, bufferValue, maxSiloCapacity);

  const daysHeight = days > maxDays ? 1 : Math.min(daysInKg / maxSiloCapacity, 1);
  const showConsumptionsKgDays = dailyConsumptionRate * days;
  const totalBuffer = Number(staticBufferKg) + Number(showConsumptionsKgDays);

  return (
    <Box display={'flex'} alignItems={'flex-start'} width={'100%'} p={4}>
      <AccordionGroup>
        <Accordion>
          <AccordionSummary
            sx={{ alignItems: 'start' }}
            aria-controls="panel1-content"
            id="panel1-header"
            expandIcon={<ChevronDown />}>
            <Box>
              <Box display={'flex'} flexDirection={'column'} mb={4}>
                <Typography variant="body1" fontWeight={800}>
                  <FormattedMessage id={`${PAGE_NAME_ID}.totalBufferTitel`} />
                </Typography>
              </Box>
              <Box display={'flex'} flexDirection={'column'} gap={1}>
                <Typography variant="h3" fontWeight={800} m={0}>
                  <FormattedNumber
                    value={totalBuffer}
                    unit={'kilogram'}
                    style="unit"
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                  />
                </Typography>
                <Typography variant="body1">
                  <FormattedMessage id={`${PAGE_NAME_ID}.totalBufferText`} />
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Divider dark />
            <Box display={'flex'} flexDirection={'column'} gap={4} mt={8}>
              <Box mb={4}>
                <Typography variant="body1" mb={2}>
                  <b>
                    <FormattedMessage id={`${PAGE_NAME_ID}.adaptiveBufferTitel`} />:
                  </b>
                  &nbsp;
                  {days}
                  &nbsp;
                  <FormattedMessage id={`${PAGE_NAME_ID}.daysConsumption`} />
                </Typography>

                <Typography variant="body1">
                  <FormattedMessage
                    id={`${PAGE_NAME_ID}.adaptiveBufferText`}
                    defaultMessage="  Based on your current consumption, {days} days worth of feed equals {increasedDays}."
                    values={{
                      days: days,
                      increasedDays: (
                        <FormattedNumber
                          value={showConsumptionsKgDays}
                          unit={'kilogram'}
                          style="unit"
                          minimumFractionDigits={0}
                          maximumFractionDigits={0}
                        />
                      ),
                    }}
                  />
                </Typography>
              </Box>

              <Box mb={4}>
                <Typography variant="body1" mb={2}>
                  <b>
                    <FormattedMessage id={`${PAGE_NAME_ID}.staticBuffer`} />:
                  </b>
                  &nbsp;
                  <FormattedNumber
                    value={bufferValue * 100}
                    unit={'percent'}
                    style="unit"
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                  />
                  &nbsp; (
                  <FormattedNumber
                    value={Math.floor(staticBufferKg)}
                    unit={'kilogram'}
                    style="unit"
                    minimumFractionDigits={0}
                    maximumFractionDigits={0}
                  />
                  )
                </Typography>
                <Typography variant="body1">
                  <FormattedMessage id={`${PAGE_NAME_ID}.additionalBufferText`} />
                </Typography>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </AccordionGroup>

      <Box m={theme.spacing(0, 2, 2, 10)} minWidth={230} display={'flex'} flexDirection={'column'} justifyContent={'flex-end'}>
        <Box border={'solid black 1px'} borderRadius={2} p={theme.spacing(0, 0, 6, 0)} sx={{ backgroundColor: '#012231' }}>
          <BufferText>
            <FormattedMessage id={`${PAGE_NAME_ID}.maxCapacity`} />
            <FormattedNumber value={Number(ofdSettings?.siloCapacity)} maximumFractionDigits={0} /> kg
          </BufferText>
          <Box
            height={'240px'}
            display={'flex'}
            flexDirection={'column-reverse'}
            position={'relative'}
            pt={2}
            sx={{ backgroundColor: theme.palette.common.white }}>
            <BufferFill
              sx={{
                height: bufferValue,
                zIndex: zIndexValues.buffer,
                background: '#40A180',
                borderTop: 'solid black 1px',
              }}>
              <OutlineText fontSize={10} textAlign={'center'} sx={{ right: 8, top: -13, zIndex: zIndexValues.buffer }}>
                <FormattedMessage id={`${PAGE_NAME_ID}.staticTitleBuffer`} />
                &nbsp;
                <FormattedNumber
                  value={bufferValue * 100}
                  unit={'percent'}
                  style="unit"
                  minimumFractionDigits={0}
                  maximumFractionDigits={0}
                />
              </OutlineText>
            </BufferFill>

            <BufferFill
              sx={{
                height: daysHeight,
                background: theme.palette.green.o50,
                zIndex: zIndexValues.days,
                borderTop: 'dashed black 1px',
              }}>
              <OutlineText
                fontSize={10}
                textAlign={'center'}
                sx={{
                  left: 8,
                  top: dailyConsumptionRate === 0 ? -18 : daysHeight < 0.93 ? -13 : 11,
                  borderRadius: daysHeight < 0.93 ? theme.spacing(2, 2, 0, 0) : theme.spacing(0, 0, 2, 2),
                }}>
                <FormattedMessage id={`${PAGE_NAME_ID}.adaptiveBuffer`} /> {days} days
              </OutlineText>
            </BufferFill>
          </Box>
        </Box>
        <LineDescription />
      </Box>
    </Box>
  );
};

export default FeedReserve;
